import {
  generateURNByKind,
  getEntityDisplayName,
  transformUrnToWitboostId,
} from '@agilelab/plugin-wb-builder-common';
import { WbAutocomplete } from '@agilelab/plugin-wb-platform';
import {
  Entity,
  EntityRelation,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { FormControl, FormHelperText } from '@material-ui/core';
import { JSONSchema7 } from 'json-schema';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { fromZodError } from 'zod-validation-error';
import { deserializeEntityRef } from '../../../api';
import { FieldExtensionUiSchema } from '../../../extensions/types';
import { extractCustomProperties, isHidden } from '../../utils';
import {
  EntityRelationsPickerOptions,
  EntityRelationsPickerProps,
  EntityRelationsPickerSchemaZod,
} from './schema';
import { UseDependencyManagerResult } from '../../MultiStepJsonForm/DependencyManager';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

export function parseOptions(
  uiSchema: FieldExtensionUiSchema<string, any>,
  schema: JSONSchema7,
): Error | EntityRelationsPickerOptions {
  const parsedPickerConfig = EntityRelationsPickerSchemaZod.safeParse({
    schema,
    uiSchema,
  });
  if (!parsedPickerConfig.success) {
    const errorMessage = `This field has been disabled due to a misconfiguration error. ${
      fromZodError(parsedPickerConfig.error).message
    }. Please contact the platform team to address this issue.`;
    return new Error(errorMessage);
  }

  const data = parsedPickerConfig.data;

  let filters: ((entity: Entity) => boolean)[] = [];
  if (data.uiSchema['ui:componentsFilter']) {
    filters.push(
      (entity: Entity) =>
        entity.spec?.type === data.uiSchema['ui:componentsFilter'],
    );
  }
  if (data.uiSchema['ui:filters'] && data.uiSchema['ui:filters'].length > 0) {
    filters = filters.concat(
      data.uiSchema['ui:filters'].map(f => {
        return (entity: Entity) => get(entity, f.key) === f.value;
      }),
    );
  }

  return {
    field: {
      name: data.uiSchema['ui:fieldName'],
      path: data.uiSchema['ui:property'],
    },
    returnFormat: data.schema.returnFormat ?? 'urn',
    relation: data.schema.relation ?? 'hasPart',
    filters: filters,
  };
}

async function fetchEntity(
  inputEntityRef: string,
  options: EntityRelationsPickerOptions,
  catalogApi: CatalogApi,
): Promise<Entity | undefined> {
  const entityRef = inputEntityRef
    ? deserializeEntityRef(inputEntityRef)
    : undefined;

  if (!entityRef) {
    return undefined;
  }

  const entity = await catalogApi.getEntityByRef(entityRef);

  if (options.field.path) {
    const subFieldEntityProperty = get(entity, options.field.path);

    const subFieldEntityRef = subFieldEntityProperty
      ? deserializeEntityRef(subFieldEntityProperty)
      : undefined;

    if (subFieldEntityRef) {
      return catalogApi.getEntityByRef(subFieldEntityRef);
    }
    return undefined;
  }

  return entity;
}

export async function fetchEntityRelations(
  formContext: any,
  options: EntityRelationsPickerOptions,
  catalogApi: CatalogApi,
): Promise<Entity[] | undefined> {
  const inputEntityRef = formContext[options.field.name];
  const entity = await fetchEntity(inputEntityRef, options, catalogApi);

  if (!entity) {
    return undefined;
  }

  const relationsRefs =
    entity.relations
      ?.filter((relation: EntityRelation) => relation.type === options.relation)
      .map((relation: EntityRelation) => relation.targetRef) ?? [];

  const relatedEntities: Entity[] = (
    await catalogApi.getEntitiesByRefs({ entityRefs: relationsRefs })
  ).items.filter((ent): ent is Entity => !!ent);

  if (options.filters.length > 0) {
    const filterEntity = (e: Entity): boolean => {
      return options.filters.every(filter => filter(e));
    };
    return relatedEntities.filter(filterEntity);
  }

  return relatedEntities;
}

function formatReturn(
  entity: Entity,
  options: EntityRelationsPickerOptions,
): string {
  switch (options.returnFormat) {
    case 'urn':
      return generateURNByKind(entity.metadata.name, entity.kind);

    default:
      return stringifyEntityRef(entity);
  }
}

export const EntityRelationsPicker = (props: EntityRelationsPickerProps) => {
  const {
    onChange,
    schema,
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    formContext,
  } = props;

  const disabled = uiSchema['ui:disabled'] || false;
  const catalogApi = useApi(catalogApiRef);
  const [value, setValue] = useState<Entity | null>(null);
  const [pickerError, setPickerError] = useState<Error | undefined>(undefined);
  const customProps = extractCustomProperties(uiSchema);

  // Extract dependency manager and field path
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const rootSchema = formContext?.rootSchema;
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  const options = parseOptions(uiSchema, schema);

  const dependentFieldValue =
    options instanceof Error ? undefined : formContext[options.field.name];

  // Cache of last successfully fetched entities.
  const cachedEntitiesRef = useRef<Entity[] | undefined>(undefined);

  useEffect(() => {
    async function setInitialValue() {
      let formDataEntityRef: string | null | undefined = null;
      if (formData) {
        formDataEntityRef = formData.startsWith('urn:dmb:')
          ? transformUrnToWitboostId(formData)
          : formData;
      }
      const formDataEntity: Entity | null = formDataEntityRef
        ? (await catalogApi.getEntityByRef(formDataEntityRef)) ?? null
        : null;
      setValue(formDataEntity);
    }

    setInitialValue();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { value: entities, loading } = useAsync(async () => {
    if (options instanceof Error) {
      setPickerError(options);
      return undefined;
    }
    setPickerError(undefined);

    setValue(null);

    const fetchedEntities = await fetchEntityRelations(
      formContext,
      options,
      catalogApi,
    );

    cachedEntitiesRef.current = fetchedEntities;
    return fetchedEntities;
  }, [dependentFieldValue, catalogApi]);

  const onSelect = useCallback(
    (_: any, val: Entity | null) => {
      if (typeof val !== 'string' && !(options instanceof Error)) {
        const ref = val ? formatReturn(val, options) : undefined;
        setValue(val);

        // Notify dependency manager to propagate changes to dependents, or fallback to onChange
        if (dependencyManager?.notifyUpdate && fieldPath) {
          dependencyManager.notifyUpdate({
            fieldPath,
            value: ref,
            source: 'user',
            intent: 'manual',
          });
        } else {
          onChange(ref);
        }
      }
    },
    [onChange, options, dependencyManager, fieldPath],
  );

  return (
    <FormControl
      required={required}
      style={{
        display: isHidden(uiSchema) ? 'none' : undefined,
      }}
    >
      <WbAutocomplete
        id={idSchema?.$id}
        value={value || null}
        loading={loading}
        onChange={onSelect}
        autoSelect
        options={entities || []}
        freeSolo={false}
        error={
          (rawErrors?.length > 0 && !formData) || pickerError !== undefined
        }
        getOptionLabel={(option: Entity) => getEntityDisplayName(option)}
        getOptionSelected={(option: Entity, val: Entity) =>
          generateURNByKind(option.metadata.name, option.kind) ===
          generateURNByKind(val.metadata.name, val.kind)
        }
        label={schema.title}
        disabled={pickerError || disabled}
        helperText={schema.description}
        required={required}
        {...customProps}
      />
      {pickerError && (
        <FormHelperText id={`${idSchema?.$id}-error-text`}>
          {pickerError.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};
